// src/Component/Header.tsx
import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/Front.png'; 
import logo2 from '../assets/Back.png'; 

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Contact Us', path: '/contact' }
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed w-full z-50 bg-white h-20 shadow-md border-b border-gray-100 flex items-center"
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 w-full flex items-center justify-between">
          
          {/* LEFT: Double Logo Section */}
          <div className="flex items-center min-w-0 max-w-[70%] md:max-w-none">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
              {/* Primary Logo */}
              <div className="h-10 sm:h-12 md:h-16 w-auto flex items-center">
                <img 
                  src={logo}   
                  alt="Primary Logo"
                  className="h-full w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                />
              </div>

              {/* Vertical Divider */}
              <div className="h-6 sm:h-8 w-[1px] bg-gray-200 mx-1"></div>

              {/* Secondary Logo */}
              <div className="h-6 sm:h-8 md:h-10 w-auto flex items-center">
                <img 
                  src={logo2}
                  alt="Secondary Logo"
                  className="h-full w-auto object-contain opacity-90 transition-all duration-300 group-hover:opacity-100"
                />
              </div>
            </Link>
          </div>

          {/* CENTER: Navigation Links */}
          <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center gap-10">
            {navLinks.map((link) => (
              <Link 
                key={link.name}
                to={link.path} 
                className="relative text-[14px] tracking-[0.05em] font-bold text-gray-800 hover:text-red-600 transition-colors duration-300 group"
              >
                {link.name.toUpperCase()}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-red-600 transition-all duration-300 group-hover:w-full rounded-full"></span>
              </Link>
            ))}
          </div>

          {/* RIGHT: Login, Demat Button & Mobile Menu Toggle */}
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            
            {/* NEW: Login Button */}
            <Link to="/login" className="hidden md:block">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white px-5 lg:px-7 py-2 rounded-full text-[14px] lg:text-[15px] font-black uppercase tracking-wider transition-all"
              >
                Login
              </motion.button>
            </Link>

            {/* Existing Demat Button */}
            <Link to="/demat/register" className="hidden md:block">
              <motion.button 
                whileHover={{ scale: 1.05, backgroundColor: "#dc2626" }}
                whileTap={{ scale: 0.95 }}
                className="bg-red-600 text-white px-6 lg:px-9 py-2.5 rounded-full text-[14px] lg:text-[15px] font-black uppercase tracking-wider shadow-lg shadow-red-100 transition-all"
              >
                Open Demat Account
              </motion.button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden text-gray-800 p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors" 
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Toggle Menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: "spring", bounce: 0, duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-white lg:hidden flex flex-col"
          >
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-100 shadow-sm">
              {/* Mobile Double Logo */}
              <div className="flex items-center gap-2 h-8 sm:h-10">
                <img src={logo} alt="Primary Logo" className="h-full w-auto object-contain" />
                <div className="h-full w-[1px] bg-gray-200 mx-1"></div>
                <img src={logo2} alt="Secondary Logo" className="h-full w-auto object-contain opacity-70" />
              </div>
              <button 
                onClick={() => setIsMobileMenuOpen(false)} 
                className="text-gray-500 hover:text-red-600 hover:bg-gray-50 p-2 rounded-full transition-all"
              >
                <X size={28} />
              </button>
            </div>
            
            <div className="flex flex-col p-4 sm:p-6 overflow-y-auto">
              {navLinks.map((link) => (
                <Link 
                  key={link.name}
                  to={link.path} 
                  className="py-4 text-lg font-bold text-gray-800 border-b border-gray-100 flex justify-between items-center hover:text-red-600 transition-colors"
                >
                  {link.name} <ChevronRight size={18} className="text-gray-400" />
                </Link>
              ))}
            </div>

            {/* NEW: Updated Mobile Menu Action Buttons */}
            <div className="mt-auto p-4 sm:p-6 bg-gray-50 border-t border-gray-100 flex flex-col gap-3">
              <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                <button className="w-full py-3 border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white font-bold text-lg rounded-xl uppercase transition-all active:scale-95">
                  Login
                </button>
              </Link>
              <Link to="/demat/register" onClick={() => setIsMobileMenuOpen(false)}>
                <button className="w-full py-3.5 bg-red-600 hover:bg-red-700 text-white font-bold text-lg rounded-xl shadow-lg shadow-red-200 uppercase transition-all active:scale-95">
                  Open Demat Account
                </button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;